<template lang="pug">
.section.section-contact
  //- NuxtImg.section-contact__pet(src="/assets/images/pet.svg" alt="" aria-hidden="true")

  .section-contact__content
    h2.section-contact__title Contact us
    //- p.section-contact__description Got a question? Drop us a line and we’ll get back to you.

    form.section-contact__form(@submit.prevent="submit" novalidate)
      // Name
      label.section-contact__label(for="name") Your name
      input#name.section-contact__input(
        v-model.trim="form.name"
        type="text"
        name="name"
        autocomplete="name"
        :aria-invalid="!!errors.name"
        required
      )
      p.section-contact__error(v-if="errors.name") {{ errors.name }}

      // Email
      label.section-contact__label(for="email") Your email
      input#email.section-contact__input(
        v-model.trim="form.email"
        type="email"
        name="email"
        autocomplete="email"
        :aria-invalid="!!errors.email"
        required
      )
      p.section-contact__error(v-if="errors.email") {{ errors.email }}

      // Message
      label.section-contact__label(for="message") Message (optional)
      textarea#message.section-contact__textarea(
        v-model.trim="form.message"
        name="message"
        rows="6"
        placeholder="How can we help?"
      )

      // Honeypot (hidden)
      input.section-contact__hp(
        v-model="form.company"
        type="text"
        name="company"
        tabindex="-1"
        aria-hidden="true"
      )

      // Actions / status
      .section-contact__actions
        button.btn(type="submit" :disabled="pending")
          | {{ pending ? 'Submitting…' : 'Submit' }}
        p.section-contact__status(role="status" aria-live="polite") {{ statusText }}
</template>

<script setup lang="ts">
type ApiOk = { ok?: boolean; message?: string }

const props = withDefaults(defineProps<{
  endpoint?: string
  successText?: string
  title?: string
  description?: string
}>(), {
  endpoint: '/api/contact',
  successText: 'Submitted successfully!',
  title: 'Contact us',
  description: 'Got a question? Drop us a line and we’ll get back to you.'
})

const form = reactive({
  name: '',
  email: '',
  message: '',
  company: '' // honeypot
})

const errors = reactive<{ name?: string; email?: string }>({})
const pending = ref(false)
const statusText = ref('')

function validate () {
  errors.name = form.name ? '' : 'Please enter your name.'
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)
  errors.email = emailOk ? '' : 'Please enter a valid email.'
  return !errors.name && !errors.email
}

async function submit () {
  statusText.value = ''
  if (!validate()) return
  // Honeypot: pretend success if filled
  if (form.company) {
    statusText.value = props.successText
    return reset()
  }

  pending.value = true
  try {
    const payload = { name: form.name, email: form.email, message: form.message }
    const res = await $fetch<ApiOk>(props.endpoint, { method: 'POST', body: payload })
    if (res && res.ok === false) throw new Error(res.message || 'Submission failed.')
    statusText.value = props.successText
    reset()
  } catch (e: any) {
    statusText.value = e?.data?.message || e?.message || 'Submission failed. Please try again.'
  } finally {
    pending.value = false
  }
}

function reset () {
  form.name = ''
  form.email = ''
  form.message = ''
  form.company = ''
}
</script>

<style lang="sass" scoped>
.section-contact
  min-height: calc(100vh - 85px)
  --pad-x: 76px
  --gap: 24px
  // colours / theme
  --Candid-Chalk: #FBF9F2
  --bg: #FF2B98
  --title: #1C1C1C
  --text: #FFFFFF
  --field-bg: var(--Candid-Chalk)
  --field-text: #1C1C1C
  --field-border: #D6D6D6
  --field-border-focus: #111111
  --error: #B4DC19
  --button-bg: #111111
  --button-text: #FFFFFF
  --border-raduis: 3px

  position: relative
  padding: 90px var(--pad-x)
  background: var(--bg)

  display: grid
  grid-template-columns: repeat(12, 1fr)
  column-gap: var(--gap)
  row-gap: 0

  text-align: center

  &__pet
    position: absolute
    bottom: 0
    right: 0
    --cols: 6
    width: calc(((100% - (2 * var(--pad-x)) - (11 * var(--gap))) / 12) * var(--cols) + (3 * var(--gap)) + var(--pad-x))
    height: auto
    pointer-events: none

  &__content
    grid-column: 1 / 8
    z-index: 1

  &__title
    color: var(--title)
    font-family: "East Sea Dokdo"
    font-size: 120px
    font-weight: 400
    line-height: 90px
    margin: 0 0 24px

  &__description
    color: var(--text)
    font-family: Sora
    font-size: 18px
    font-weight: 600
    line-height: 140%
    margin: 0 0 20px

  // FORM GRID (12 cols like hero)
  &__form
    display: grid
    grid-template-columns: repeat(12, 1fr)
    column-gap: var(--gap)
    row-gap: 12px

  // labels occupy 3 cols, fields 9 cols
  &__label
    grid-column: 1 / 4
    align-self: center
    color: var(--text)
    font-family: Sora
    font-size: 16px
    font-weight: 700

  &__input,
  &__textarea
    grid-column: 4 / 13
    width: 100%
    padding: 12px 14px
    border-radius: var(--border-raduis)
    border: 1px solid var(--field-border)
    background: var(--field-bg)
    color: var(--field-text)
    font-family: Sora
    font-size: 16px
    outline: none
    transition: border-color .15s ease

    &:focus
      border-color: var(--field-border-focus)

    &[aria-invalid="true"]
      border-color: var(--error)

  &__textarea
    resize: vertical
    min-height: 140px

  &__error
    grid-column: 4 / 13
    margin-top: 2px
    color: var(--error)
    font-size: 14px
    line-height: 1.2

  &__hp
    position: absolute !important
    left: -9999px !important
    width: 1px
    height: 1px
    overflow: hidden

  &__actions
    grid-column: 4 / 13
    display: flex
    flex-wrap: wrap
    gap: 12px
    align-items: center
    margin-top: 12px

  &__button
    padding: 12px 18px
    border-radius: var(--border-raduis)
    border: none
    background: var(--button-bg)
    color: var(--button-text)
    font-weight: 700
    cursor: pointer
    transition: opacity .15s ease

    &:disabled
      opacity: .6
      cursor: not-allowed

  &__status
    color: var(--text)
    font-size: 14px

  &__label,
  &__input,
  &__textarea,
  &__error,
  &__actions
    grid-column: 1 / 13
    text-align: left

// breakpoints mirroring your hero styles
@media (max-width: 940px)
  .section-contact
    text-align: center
    padding: 40px var(--pad-x)

    &__content
      grid-column: 1 / 13

    &__title
      font-size: 100px
      line-height: 70px

    &__description
      margin-left: auto
      margin-right: auto

    &__pet
      --cols: 8

@media (max-width: 768px)
  .section-contact
    padding: 20px

    &:deep(.btn)
      width: 100%

    &__title
      font-size: 80px
      line-height: 56px

@media (max-width: 650px)
  .section-contact
    &__title
      font-size: 64px
      line-height: 44px

    &__description
      font-size: 16px

    &__pet
      width: 100%

@media (max-width: 600px)
  .section-contact
    min-height: calc(100vh - 60px)

@media (max-width: 500px)
  .section-contact
    &__title
      font-size: 52px
      line-height: 36px

    &__description
      font-size: 14px

@media (max-width: 380px)
  .section-contact
    &__title
      font-size: 44px
      line-height: 32px

    &__description
      font-size: 12px
</style>
  